import React, { useMemo, useState } from "react";
import { Input } from "reactstrap";

import ShellCard from "../../../../components/ShellCard";
import { AUTOPILOT_COLORS, autopilotInsetCardStyle } from "../../../../components/autopilot/styles";
import { calcularPreviaPrazo, isoLocal } from "./calcularPreviaPrazo";
import { estaLigado } from "./paramParaControle";

// Prévia ilustrativa do prazo de entrega (H6, PO1-3264), só no assunto "Prazo de
// entrega" da tela do Embarcador. Inputs locais (emissão e prazo da tabela) +
// os valores ATUAIS do formulário (tipo de dias, dias adicionais, hora de corte,
// feriados), inclusive os ainda não salvos — o usuário vê o efeito antes de salvar.
// Lê os parâmetros pelo Id do catálogo, não pelo nome.
export const IDS_PRAZO = {
  diasAdicionais: 1,
  horaCorte: 2,
  tipoDias: 3,
  feriadoOrigem: 4,
  feriadoDestino: 5,
};

export const AVISO_PREVIA = "Prévia ilustrativa. Ainda sem efeito no cálculo até o PO1-2886.";

const DIAS_SEMANA = ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"];
const ROTULO_TIPO = { DU: "dias úteis (pula sábado e domingo)", DU_S: "dias úteis com sábado (pula só domingo)", DC: "dias corridos" };

function formatarData(d) {
  const [a, m, dia] = isoLocal(d).split("-");
  return `${dia}/${m}/${a}`;
}

const labelStyle = {
  display: "block",
  marginBottom: "0.3rem",
  color: AUTOPILOT_COLORS.muted,
  fontSize: "0.72rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};

export default function PreviaPrazoEntrega({ valoresAtuais }) {
  const hoje = useMemo(() => isoLocal(new Date()), []);
  const [dataEmissao, setDataEmissao] = useState(hoje);
  const [horaEmissao, setHoraEmissao] = useState("17:20");
  const [prazoTabela, setPrazoTabela] = useState("4");
  const [contarDiaEmissao, setContarDiaEmissao] = useState(false);

  // valoresAtuais é indexado pelo idParametro (String).
  const valorDoId = (id) => (valoresAtuais ? valoresAtuais[String(id)] : undefined);

  const diasAdicionais = valorDoId(IDS_PRAZO.diasAdicionais);
  const horaCorte = valorDoId(IDS_PRAZO.horaCorte);
  const tipoDias = valorDoId(IDS_PRAZO.tipoDias) || "DU";
  const consideraFeriado = estaLigado(valorDoId(IDS_PRAZO.feriadoOrigem)) || estaLigado(valorDoId(IDS_PRAZO.feriadoDestino));

  const resultado = calcularPreviaPrazo({
    dataEmissao: `${dataEmissao}T${horaEmissao || "00:00"}`,
    prazoTabelaDias: prazoTabela,
    diasAdicionais,
    tipoDias,
    horaCorte,
    contarDiaEmissao,
    consideraFeriado,
  });

  const adicionais = parseInt(diasAdicionais, 10) || 0;
  const tabela = parseInt(prazoTabela, 10) || 0;

  return (
    <ShellCard
      eyebrow="Simulação"
      title="Prévia do prazo de entrega"
      description="Veja como os parâmetros acima mudam a data prevista. Altere um valor e a prévia acompanha, mesmo antes de salvar."
    >
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: "0.85rem" }}>
        <div>
          <span style={labelStyle}>Data de emissão</span>
          <Input type="date" value={dataEmissao} onChange={(e) => setDataEmissao(e.target.value)} />
        </div>
        <div>
          <span style={labelStyle}>Hora de emissão</span>
          <Input type="time" value={horaEmissao} onChange={(e) => setHoraEmissao(e.target.value)} />
        </div>
        <div>
          <span style={labelStyle}>Prazo da tabela (dias)</span>
          <Input type="number" min={0} value={prazoTabela} onChange={(e) => setPrazoTabela(e.target.value)} />
        </div>
        <div>
          <span style={labelStyle}>Dia de início</span>
          <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", minHeight: 38, margin: 0, color: AUTOPILOT_COLORS.text, fontSize: "0.84rem", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={contarDiaEmissao}
              onChange={(e) => setContarDiaEmissao(e.target.checked)}
              style={{ accentColor: "var(--theme-accent)" }}
            />
            Conta como dia 1
          </label>
        </div>
      </div>

      <div style={{ ...autopilotInsetCardStyle, marginTop: "1rem", padding: "0.9rem 1rem" }}>
        {resultado ? (
          <>
            <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.76rem", textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Entrega prevista
            </div>
            <div style={{ color: AUTOPILOT_COLORS.title, fontSize: "1.25rem", fontWeight: 700, marginTop: "0.15rem" }}>
              {DIAS_SEMANA[resultado.dataPrevista.getDay()]}, {formatarData(resultado.dataPrevista)}
            </div>
            <div style={{ color: AUTOPILOT_COLORS.text, fontSize: "0.82rem", lineHeight: 1.55, marginTop: "0.5rem" }}>
              {resultado.aposCorte
                ? `Emissão depois da hora de corte (${horaCorte}): a contagem começa em ${formatarData(resultado.inicio)}.`
                : horaCorte
                  ? `Emissão dentro da hora de corte (${horaCorte}): a contagem começa no próprio dia ${formatarData(resultado.inicio)}.`
                  : "Sem hora de corte definida: a contagem começa no dia da emissão."}{" "}
              Prazo de {resultado.totalDias} dia{resultado.totalDias === 1 ? "" : "s"} ({tabela} da tabela + {adicionais} adiciona{adicionais === 1 ? "l" : "is"}) em{" "}
              {ROTULO_TIPO[resultado.tipoDias]}
              {resultado.tipoDias !== "DC" ? `, feriados nacionais ${consideraFeriado ? "considerados" : "não considerados"}` : ""}.
              {resultado.diasPulados ? ` ${resultado.diasPulados} dia${resultado.diasPulados === 1 ? "" : "s"} pulado${resultado.diasPulados === 1 ? "" : "s"} no caminho.` : ""}
            </div>
          </>
        ) : (
          <div style={{ color: AUTOPILOT_COLORS.muted, fontSize: "0.84rem" }}>Informe uma data de emissão válida para ver a prévia.</div>
        )}
      </div>

      <div style={{ marginTop: "0.75rem", color: AUTOPILOT_COLORS.statusWarning, fontSize: "0.78rem", fontWeight: 600 }}>{AVISO_PREVIA}</div>
    </ShellCard>
  );
}
